import React, { useState } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import cn from 'bem-cn';

import radialBackground from '../../images/radial.svg';

import { ShareMethod, shareUrl } from '../../utils/share';

import { useNavigateBack } from '../../hooks';
import { useGetUserByLoginOrPublicIdQuery } from '../../store/services';
import { RoutePaths, ProfileNestedRoutes } from '../../constants';

import { WebAppLayout } from '../../layouts/WebAppLayout';
import { Stack, ImageWithFadeIn, Icon, Popover, Tabs } from '../../components/UI';
import { TabItem } from '../../components/UI/Tabs';
import { HeaderElements } from '../../components/Header';
import { NavigationElements } from '../../components/Navigation';
import { Profile } from '../../components/Profile/Profile';

import { ProfilePageMeta } from './ProfilePage.meta';
import './ProfilePage.scss';

const b = cn('profile-page');

export const ProfilePage: React.FC = () => {
    const { loginOrPublicId } = useParams<{ loginOrPublicId: string }>();

    const handleBackButtonClick = useNavigateBack();

    const { data: user, isError } = useGetUserByLoginOrPublicIdQuery(
        { loginOrPublicId: loginOrPublicId || '' },
        { skip: !loginOrPublicId },
    );

    const [headerShareButtonPopoverOpen, setHeaderShareButtonPopoverOpen] = useState(false);

    const shareProfile = async () => {
        return await shareUrl({
            title: 'Промптхаб — платформа обмена идеями, как использовать Алису AI и другие нейросети',
            text: `Профиль ${user?.name || loginOrPublicId} в Промптхабе`,
            url: `https://${window.location.hostname}/prompthub/users/${loginOrPublicId}`,
        });
    };

    const handleProfileShareButtonClick = async () => {
        await shareProfile();
    };

    const handleHeaderShareButtonClick = async () => {
        const shareResult = await shareProfile();

        if (shareResult?.method === ShareMethod.Clipboard) {
            setHeaderShareButtonPopoverOpen(true);
            setTimeout(() => {
                setHeaderShareButtonPopoverOpen(false);
            }, 2000);
        }
    };

    const headerElements: HeaderElements = {
        desktop: {
            left: {
                type: 'logo',
            },
            right: {
                type: 'profile',
            },
        },
        mobile: {
            left: {
                type: 'backButton',
                onClick: handleBackButtonClick,
            },
            center: {
                type: 'custom',
                render: () => {
                    return <span className={b('header-text')}>ИИ профиль</span>;
                },
            },
            right: {
                type: 'custom',
                render: () => {
                    return (
                        <Popover
                            content="Ссылка скопирована"
                            position="bottom-left"
                            open={headerShareButtonPopoverOpen}
                            contentClassName="nowrap"
                        >
                            <Icon name="share" size={24} onClick={handleHeaderShareButtonClick} />
                        </Popover>
                    );
                },
            },
        },
    };

    const emptyNavigationElements: NavigationElements = {
        desktop: [],
        mobile: [],
    };

    const tabItems: TabItem[] = [
        {
            text: 'Промпты',
            icon: 'prompt',
            to: [RoutePaths.UserProfile, loginOrPublicId].join('/'),
        },
        {
            text: 'Рейтинг',
            icon: 'rating',
            to: [RoutePaths.UserProfile, loginOrPublicId, ProfileNestedRoutes.Rating].join('/'),
        },
        {
            text: 'Брелоки',
            icon: 'crown',
            to: [RoutePaths.UserProfile, loginOrPublicId, ProfileNestedRoutes.Achievements].join('/'),
        },
    ];

    if (isError) {
        return <Navigate to={RoutePaths.NotFound} replace />;
    }

    return (
        <>
            <ProfilePageMeta />
            <WebAppLayout headerElements={headerElements} navigationElements={emptyNavigationElements}>
                <Stack spacing={24} className={b()}>
                    <div className={b('background')}>
                        <ImageWithFadeIn src={radialBackground} alt="" />
                    </div>
                    <Stack spacing={24}>
                        {user && (
                            <Profile
                                loginOrPublicId={loginOrPublicId}
                                avatarSrc={user.image}
                                fullName={user.name}
                                isProfilePublic
                                onShareButtonClick={handleProfileShareButtonClick}
                            />
                        )}
                        <div className={b('tabs')}>
                            <Tabs items={tabItems} />
                        </div>
                        <div className={b('content')}>
                            <Outlet />
                        </div>
                    </Stack>
                </Stack>
            </WebAppLayout>
        </>
    );
};
